import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './Dashboard.css';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });
};

const firstNameOf = (user) => {
  if (!user) return 'there';
  if (user.firstName) return user.firstName;
  const full = (user.fullName || '').trim();
  if (full) return full.split(/\s+/)[0];
  return user.username || 'there';
};

const greetingWord = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

// Parse a DB datetime "YYYY-MM-DD HH:MM:SS" as local wall-clock — consistent
// with how the rest of the app shows meeting times.
const parseDbDateTime = (v) => {
  if (!v) return null;
  const m = String(v).trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) { const d = new Date(v); return isNaN(d.getTime()) ? null : d; }
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  return isNaN(d.getTime()) ? null : d;
};

const fmtMoney = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(Number(n) || 0);

const fmtDueLabel = (v) => {
  const d = parseDbDateTime(v);
  if (!d) return 'No due date';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / 86400000);
  if (diff < 0) return `Overdue · ${d.toLocaleDateString()}`;
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  if (diff <= 6) return `Due ${d.toLocaleDateString(undefined, { weekday: 'long' })}`;
  return `Due ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
};

const fmtTimeRange = (start, end) => {
  const s = parseDbDateTime(start);
  if (!s) return '';
  const opts = { hour: 'numeric', minute: '2-digit' };
  const sTxt = s.toLocaleTimeString([], opts);
  const e = parseDbDateTime(end);
  return e ? `${sTxt} – ${e.toLocaleTimeString([], opts)}` : sTxt;
};

const relativeTime = (v) => {
  const d = parseDbDateTime(v);
  if (!d) return '';
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ACTIVITY_ICONS = { Project: '🏠', 'AOS_Quotes': '📄', 'AOS_Invoices': '🧾', Tasks: '✔️', Meetings: '📅' };

/**
 * Operational home dashboard. KPI cards, Today's Priorities, Upcoming Meetings
 * and Recent Activity, all from live records (dashboard.php). Cards and rows are
 * clickable: KPIs open the module list; rows open the underlying record.
 */
const Dashboard = ({ user, onNavigate, onOpenRecord, headerRight }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/dashboard.php?user_id=${encodeURIComponent(user?.id || '')}`);
      const json = await res.json();
      if (json.success) setData(json);
      else setError(json.message || 'Failed to load dashboard.');
    } catch (e) {
      setError('Failed to load dashboard: ' + e.message);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const priorities = data?.priorities || [];
  const meetings = data?.meetings || [];
  const activity = data?.activity || [];

  const cards = [
    { key: 'projects', label: 'Active Projects', value: kpis.activeProjects ?? '—', icon: '🏠', link: 'View all projects', module: 'Project' },
    { key: 'pipeline', label: 'Pipeline Value', value: fmtMoney(kpis.pipelineValue), icon: '🎯', link: 'View pipeline', module: 'AOS_Quotes' },
    { key: 'proposals', label: 'Open Proposals', value: kpis.openProposals ?? '—', icon: '📄', link: 'View proposals', module: 'AOS_Quotes' },
    { key: 'tasks', label: 'Tasks This Week', value: kpis.tasksThisWeek ?? '—', icon: '📅', link: 'View tasks', module: 'Tasks' },
  ];

  return (
    <main className="dashboard">
      <div className="dashboard-topbar">
        <div className="dashboard-greeting">
          <h1>{greetingWord()}, {(data?.companyName && data.companyName.trim()) ? data.companyName : firstNameOf(user)}</h1>
          <p>Here's what needs your attention today.</p>
        </div>
        {headerRight && <div className="dashboard-topbar-right">{headerRight}</div>}
      </div>

      {error && <div className="dashboard-error">{error}</div>}

      {/* KPI cards */}
      <div className="dashboard-kpis">
        {cards.map(c => (
          <button key={c.key} type="button" className="kpi-card" onClick={() => onNavigate(c.module)}>
            <span className="kpi-icon">{c.icon}</span>
            <span className="kpi-label">{c.label}</span>
            <span className="kpi-value">{loading ? '…' : c.value}</span>
            <span className="kpi-link">{c.link} →</span>
          </button>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Today's Priorities */}
        <section className="dash-panel dash-priorities">
          <div className="dash-panel-head">
            <h2>Today's Priorities</h2>
            <button type="button" className="dash-panel-link" onClick={() => onNavigate('Tasks')}>View all tasks →</button>
          </div>
          {loading ? (
            <div className="dash-empty">Loading…</div>
          ) : priorities.length === 0 ? (
            <div className="dash-empty">No open tasks. You're all caught up.</div>
          ) : (
            <ul className="dash-list">
              {priorities.map(t => (
                <li key={t.id} className="dash-row" onClick={() => onOpenRecord('Tasks', t.id)}>
                  <div className="dash-row-main">
                    <span className="dash-row-title">{t.name}</span>
                    {t.context && <span className="dash-row-sub">{t.context}</span>}
                  </div>
                  <div className="dash-row-meta">
                    <span className={`dash-due ${/Overdue/.test(fmtDueLabel(t.date_due)) ? 'overdue' : ''}`}>{fmtDueLabel(t.date_due)}</span>
                    {t.priority && <span className={`dash-pill priority-${String(t.priority).toLowerCase()}`}>{t.priority}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="dashboard-side">
          {/* Upcoming Meetings */}
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Upcoming Meetings</h2>
              <button type="button" className="dash-panel-link" onClick={() => onNavigate('Meetings')}>View calendar →</button>
            </div>
            {loading ? (
              <div className="dash-empty">Loading…</div>
            ) : meetings.length === 0 ? (
              <div className="dash-empty">No upcoming meetings.</div>
            ) : (
              <ul className="dash-list">
                {meetings.map(m => {
                  const d = parseDbDateTime(m.date_start);
                  return (
                    <li key={m.id} className="dash-row dash-meeting" onClick={() => onOpenRecord('Meetings', m.id)}>
                      <div className="dash-date-badge">
                        <span className="dash-date-mon">{d ? d.toLocaleDateString(undefined, { month: 'short' }).toUpperCase() : ''}</span>
                        <span className="dash-date-day">{d ? d.getDate() : '–'}</span>
                      </div>
                      <div className="dash-row-main">
                        <span className="dash-row-time">{fmtTimeRange(m.date_start, m.date_end)}</span>
                        <span className="dash-row-title">{m.name}</span>
                        {m.location && <span className="dash-row-sub">{m.location}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Recent Activity */}
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Recent Activity</h2>
            </div>
            {loading ? (
              <div className="dash-empty">Loading…</div>
            ) : activity.length === 0 ? (
              <div className="dash-empty">No recent activity.</div>
            ) : (
              <ul className="dash-list">
                {activity.map((a, i) => {
                  const who = a.modified_user_id && user?.id && a.modified_user_id === user.id
                    ? 'You' : (a.modified_by_name || '');
                  return (
                    <li key={`${a.module}-${a.id}-${i}`} className="dash-row dash-activity" onClick={() => onOpenRecord(a.module, a.id)}>
                      <span className="dash-activity-icon">{ACTIVITY_ICONS[a.module] || '•'}</span>
                      <div className="dash-row-main">
                        <span className="dash-row-title">
                          {a.type_label} <strong>{a.name}</strong> {a.action}{who ? ` by ${who}` : ''}
                        </span>
                      </div>
                      <span className="dash-activity-time">{relativeTime(a.date_modified)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
